import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ArrowDropUp as ArrowUpIcon,
  ArrowDropDown as ArrowDownIcon,
  UnfoldMore as ArrowUpDownIcon,
} from '@mui/icons-material';
import {
  getEditableCollectionProperties,
  getFieldTitle,
  getLookupConfig,
  renderLookupCellValue,
  renderCollectionCellInput,
  renderCollectionCellDisplay,
  getFieldSchema,
} from '../../../../../utils/collectionHelpers';
import { normalizePropertyName } from '../../../base/SchemaFormBuilder';

export interface CollectionTableProps {
  items: any[];
  schemaKey: string | null;
  schema: Record<string, any>;
  editingRowId: string | null;
  editingRowData: Record<string, any>;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  lookupCache: Record<string, Record<string, any>>;
  saving: boolean;
  
  // Event handlers
  onStartEdit: (item: any) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditCellChange: (field: string, value: any) => void;
  onSort: (column: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

/**
 * Reusable collection items table with inline editing, sorting, and actions
 */
export const CollectionTable: React.FC<CollectionTableProps> = ({
  items,
  schemaKey,
  schema,
  editingRowId,
  editingRowData,
  sortBy,
  sortOrder,
  lookupCache,
  saving,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditCellChange,
  onSort,
  onEdit,
  onDelete,
}) => {
  // Get editable properties from schema
  // Handles both array schema, object schema, and flat x-control schemas
  const editableProperties = getEditableCollectionProperties(schema);
  // properties mirrors editableProperties for field lookups (title, schema, etc.)
  const properties = editableProperties;

  const handleSortClick = (columnKey: string) => {
    onSort(columnKey);
  };

  if (items.length === 0) {
    return (
      <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
        No items in this collection yet.
      </Typography>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        mb: 2,
        overflowX: 'auto',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: '1px solid #E6E7EB',
        borderRadius: '8px',
      }}
    >
      <Table size="small">
        {/* Table Head */}
        <TableHead>
          <TableRow sx={{ backgroundColor: '#F2F3F5' }}>
            {/* ID Column */}
            <TableCell
              onClick={() => handleSortClick('id')}
              sx={{
                fontWeight: 600,
                color: '#333333',
                borderBottom: '1px solid #E6E7EB',
                minWidth: 150,
                cursor: 'pointer',
                userSelect: 'none',
                padding: '12px 16px',
                '&:hover': { backgroundColor: '#E6E7EB' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                ID
                {sortBy === 'id' ? (
                  sortOrder === 'asc' ? (
                    <ArrowUpIcon fontSize="small" />
                  ) : (
                    <ArrowDownIcon fontSize="small" />
                  )
                ) : (
                  <ArrowUpDownIcon fontSize="small" sx={{ opacity: 0.5 }} />
                )}
              </Box>
            </TableCell>

            {/* Data Properties */}
            {Object.keys(editableProperties).map((propKey) => (
              <TableCell
                key={propKey}
                onClick={() => handleSortClick(propKey)}
                sx={{
                  fontWeight: 600,
                  minWidth: 150,
                  cursor: 'pointer',
                  userSelect: 'none',
                  padding: '12px 16px',
                  '&:hover': { backgroundColor: '#E6E7EB' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {getFieldTitle(properties, propKey)}
                  {sortBy === propKey ? (
                    sortOrder === 'asc' ? (
                      <ArrowUpIcon fontSize="small" />
                    ) : (
                      <ArrowDownIcon fontSize="small" />
                    )
                  ) : (
                    <ArrowUpDownIcon fontSize="small" sx={{ opacity: 0.5 }} />
                  )}
                </Box>
              </TableCell>
            ))}

            {/* Actions Column */}
            <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 120 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>

        {/* Table Body */}
        <TableBody>
          {items.filter(Boolean).map((item) => (
            <TableRow key={item.id} hover={editingRowId !== item.id} sx={{ height: 72 }}>
              {/* ID Column */}
              <TableCell sx={{ py: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                  }}
                >
                  {String(item.id ?? '').startsWith('temp-')
                    ? '(auto-generated)'
                    : String(item.id ?? '').substring(0, 12) + '...'}
                </Typography>
              </TableCell>

              {/* Data Properties */}
              {Object.keys(editableProperties).map((propKey) => {
                const fieldSchema = getFieldSchema(properties, propKey);
                const lookupConfig = getLookupConfig(properties, propKey);
                const itemData = item.data ?? {};
                const normalizedKey = normalizePropertyName(propKey);
                const cellValue = itemData[normalizedKey] ?? itemData[propKey];

                return (
                  <TableCell key={`${item.id}-${propKey}`} sx={{ py: 1, verticalAlign: 'middle' }}>
                    {editingRowId === item.id ? (
                      renderCollectionCellInput(
                        fieldSchema,
                        editingRowData[normalizedKey] ?? editingRowData[propKey],
                        (value) => onEditCellChange(normalizedKey, value)
                      )
                    ) : (
                      <Box sx={{ fontSize: '0.875rem', wordBreak: 'break-word', display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                        {lookupConfig ? (
                          // Lookup field: render chips using cached display values
                          renderLookupCellValue(
                            lookupConfig,
                            cellValue,
                            lookupCache[lookupConfig.source]
                          )
                        ) : (
                          renderCollectionCellDisplay(fieldSchema, cellValue)
                        )}
                      </Box>
                    )}
                  </TableCell>
                );
              })}

              {/* Actions Column */}
              <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                {editingRowId === item.id ? (
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Save">
                      <IconButton
                        size="small"
                        onClick={onSaveEdit}
                        color="success"
                        disabled={saving}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        onClick={onCancelEdit}
                        disabled={saving}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Edit inline/Modal">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(item.id)}
                        sx={{ color: '#005e5d' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => onDelete(item.id)}
                        sx={{
                          color: '#D32F2F',
                          '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CollectionTable;
